import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';

/**
 * Delivery Board lifecycle columns. Received parts drop out of every
 * working column server-side and surface on the `received` audit tab
 * (newest receivedAt first); receiving happens through
 * POST /delivery/receive (see useReceiveParts).
 */
export type DeliveryBoardFilter =
    | 'active'
    | 'part-changes'
    | 'backordered'
    | 'unfulfilled'
    | 'received';

/** The kanban's columns — every category except the received audit tab. */
export type DeliveryBoardWorkingFilter = Exclude<DeliveryBoardFilter, 'received'>;

export type DeliveryBoardEtaFilter = 'today' | 'tomorrow' | 'this-week' | 'overdue';
export type DeliveryBoardGrouping = 'ro' | 'supplier' | 'none';
export type DeliveryBoardSortKey = 'eta' | 'orderSize' | 'orderPlacementTime' | 'orderStartTime';
export type DeliveryBoardSortDirection = 'asc' | 'desc';
export type DeliveryBoardView = 'list' | 'board';

export interface DeliveryBoardSort {
    key: DeliveryBoardSortKey;
    dir: DeliveryBoardSortDirection;
}

export interface DeliveryBoardRow {
    /** The orderPart id — what POST /delivery/receive expects. */
    _id: string;
    orderId: string;
    supplierOrderId: string | null;
    roNumber: string;
    customerName: string | null;
    vehicle: string;
    vin: string;
    timeStarted: Date;
    timePlaced: Date | null;
    description: string;
    partNumber: string;
    vendorName: string | null;
    vendorEmail: string | null;
    vendorPhone: string | null;
    contactName: string | null;
    eta: {
        date: Date | null;
        label: string;
    };
    originalEta: {
        date: Date | null;
        label: string;
    } | null;
    listPrice: number | null;
    buyPrice: number | null;
    grossMargin: number | null;
    orderSize: number | null;
    groupingKey: string | null;
    categories: DeliveryBoardFilter[];
    /** Unfulfilled = cancelled/returned by the shop and not re-ordered; nothing is coming. */
    state: 'in-flight' | 'unfulfilled';
    /** Unfulfilled rows only: whether the part was cancelled or returned. */
    action: 'cancellation' | 'return' | null;
    unfulfilledAt: Date | null;
    backordered: boolean;
    received: boolean;
    receivedAt: string | null;
    receivedByName: string | null;
    /** order.timeCreated — drives the receive opt-in gate. */
    orderCreatedAt: Date | null;
}

/**
 * An ordered row list a shift+click range can span, with an `id` identifying
 * which list it is (the list view's page, or one kanban column). Row indices
 * only mean anything against the list they came from, so the two travel
 * together and a range never leaks across lists.
 */
export interface DeliveryBoardRangeList {
    id: string;
    rows: DeliveryBoardRow[];
}

interface DeliveryBoardFeedInput {
    limit: number;
    offset: number;
    locationIds: Array<string> | 'all';
    filters: DeliveryBoardFilter[];
    etaFilters: DeliveryBoardEtaFilter[];
    sort: DeliveryBoardSort;
    grouping: DeliveryBoardGrouping;
    search?: string;
}

interface DeliveryBoardFeedResponse {
    rows: DeliveryBoardRow[];
}

export const normalizeDeliveryBoardRows = (rows: DeliveryBoardRow[]): DeliveryBoardRow[] => (
    rows.map((row) => ({
        ...row,
        timeStarted: new Date(row.timeStarted),
        timePlaced: row.timePlaced ? new Date(row.timePlaced) : null,
        eta: {
            ...row.eta,
            date: row.eta.date ? new Date(row.eta.date) : null,
        },
        originalEta: row.originalEta
            ? {
                ...row.originalEta,
                date: row.originalEta.date ? new Date(row.originalEta.date) : null,
            }
            : null,
        unfulfilledAt: row.unfulfilledAt ? new Date(row.unfulfilledAt) : null,
        orderCreatedAt: row.orderCreatedAt ? new Date(row.orderCreatedAt) : null,
    }))
);

export const DELIVERY_BOARD_FEED_PATH = '/delivery/board/feed';

const getDeliveryBoardFeed = async (
    sessionId: string,
    input: DeliveryBoardFeedInput,
): Promise<DeliveryBoardFeedResponse> => {
    const { data } = await axios.post(`${API_URL}${DELIVERY_BOARD_FEED_PATH}`, input, {
        headers: {
            Authorization: sessionId,
        },
    });

    return {
        rows: normalizeDeliveryBoardRows(data.rows ?? []),
    };
};

/**
 * `limit` is the display page size. One extra row is over-fetched purely to
 * answer hasNextPage (callers render `rows.slice(0, limit)` and treat
 * `rows.length > limit` as "there's another page"), while the paging offset
 * advances by the display size — otherwise the sentinel row would be skipped
 * at every page boundary.
 */
export const useDeliveryBoardFeed = (
    locationIds: Array<string> | 'all',
    page: number,
    limit: number,
    filters: DeliveryBoardFilter[],
    etaFilters: DeliveryBoardEtaFilter[],
    sort: DeliveryBoardSort,
    grouping: DeliveryBoardGrouping,
    search: string,
    options?: { enabled?: boolean },
) => {
    const sessionContext = useContext(SessionContext);
    const offset = (page - 1) * limit;
    const isEnabledFromOptions = options?.enabled ?? true;

    return useQuery({
        queryKey: [
            DELIVERY_BOARD_FEED_PATH,
            locationIds === 'all' ? 'all' : locationIds.join(','),
            page,
            limit,
            filters.join(','),
            etaFilters.join(','),
            `${sort.key}:${sort.dir}`,
            grouping,
            search,
        ],
        queryFn: () => getDeliveryBoardFeed(
            sessionContext?.sessionId ?? '',
            {
                locationIds,
                limit: limit + 1,
                offset,
                filters,
                etaFilters,
                sort,
                grouping,
                search,
            },
        ),
        enabled: isEnabledFromOptions
            && !!sessionContext?.sessionId
            && (locationIds === 'all' || locationIds.length > 0),
        refetchInterval: 1000 * 60 * 5,
    });
};
